import { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import apiClient from '../lib/api-client';
import femaRegionsMap from '../assets/fema_regions_map.png';

const FEMAReportModal = ({ show, onHide, onGenerate, onGenerateMultiple }) => {
  const [region, setRegion] = useState(1);
  const [scenario, setScenario] = useState('mean');
  const [regions, setRegions] = useState([]);
  const [mode, setMode] = useState('single'); // 'single' | 'multi'
  const [selectedRegions, setSelectedRegions] = useState([]);

  // Fetch region names when modal first shows
  useEffect(() => {
    if (!show) return;

    setRegion(1);
    setScenario('mean');
    setMode('single');
    setSelectedRegions([]);

    const fetchRegions = async () => {
      try {
        const data = await apiClient.getFEMARegions();
        setRegions(data);
      } catch (e) {
        console.error('Failed to fetch FEMA regions', e);
        setRegions(
          Array.from({ length: 10 }, (_, i) => ({ id: i + 1, name: '' }))
        );
      }
    };

    if (regions.length === 0) {
      fetchRegions();
    }
  }, [show]);

  const handleSubmitSingle = () => {
    onGenerate(Number(region), scenario);
    onHide();
  };

  const handleSubmitMultiple = () => {
    const regionsToDownload = selectedRegions.length === regions.length
      ? regions.map(r => r.id)
      : [...selectedRegions];
    onGenerateMultiple(regionsToDownload, scenario);
    onHide();
  };

  const toggleRegion = (id) => {
    setSelectedRegions(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const toggleAllRegions = () => {
    if (selectedRegions.length === regions.length) {
      setSelectedRegions([]);
    } else {
      setSelectedRegions(regions.map(r => r.id));
    }
  };

  const allSelected = regions.length > 0 && selectedRegions.length === regions.length;

  return (
    <Modal show={show} onHide={onHide} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">
          <i className="fas fa-file-download me-2 text-primary" />
          Generate FEMA Report
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-main mb-2">
          Generate a PDF report with 7-day outage forecasts, estimated restoration durations, and weather insights for your selected FEMA region. Choose "Max" for worst-case, "Mean" for expected, or "Min" for best-case scenario.
        </p>

        <hr />

        {mode === 'single' ? (
          <>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>FEMA Region</Form.Label>
                <Form.Select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                >
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>
                      Region {r.id}{r.name ? ` \u2014 ${r.name}` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Scenario</Form.Label>
                <Form.Select
                  value={scenario}
                  onChange={(e) => setScenario(e.target.value)}
                >
                  <option value="min">Minimum</option>
                  <option value="mean">Mean</option>
                  <option value="max">Maximum</option>
                </Form.Select>
              </Form.Group>
            </Form>

            <div className="mb-3">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none fw-semibold"
                onClick={() => setMode('multi')}
              >
                Download multiple reports <i className="fas fa-arrow-right ms-1"></i>
              </button>
            </div>
          </>
        ) : (
          <>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Scenario</Form.Label>
                <Form.Select
                  value={scenario}
                  onChange={(e) => setScenario(e.target.value)}
                >
                  <option value="min">Minimum</option>
                  <option value="mean">Mean</option>
                  <option value="max">Maximum</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Select Regions</Form.Label>
                <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {/* All Regions toggle */}
                  <Form.Check
                    type="checkbox"
                    id="region-all"
                    label={<span className="fw-semibold">All Regions</span>}
                    checked={allSelected}
                    onChange={toggleAllRegions}
                    className="mb-2 pb-2 border-bottom"
                  />
                  {regions.map((r) => (
                    <Form.Check
                      key={r.id}
                      type="checkbox"
                      id={`region-${r.id}`}
                      label={`Region ${r.id}${r.name ? ` \u2014 ${r.name}` : ''}`}
                      checked={selectedRegions.includes(r.id)}
                      onChange={() => toggleRegion(r.id)}
                      className="mb-1"
                    />
                  ))}
                </div>
              </Form.Group>
            </Form>

            <div className="mb-3">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none fw-semibold"
                onClick={() => setMode('single')}
              >
                <i className="fas fa-arrow-left me-1"></i> Back to single report
              </button>
            </div>
          </>
        )}

        {/* FEMA Regions Reference Map */}
        <div className="text-center mt-3">
          <p className="text-muted small mb-2">FEMA Regions Reference Map</p>
          <img
            src={femaRegionsMap}
            alt="FEMA Regions Map showing all 10 regions across the United States"
            className="img-fluid rounded border"
            style={{ maxHeight: '280px' }}
          />
        </div>

        <p className="text-main small mt-3">
          <i className="fas fa-info-circle me-1"></i>
          Once you click "Generate Report", the download will start automatically. If multiple reports is selected, the browser may ask permission to allow multiple file downloads.
        </p>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        {mode === 'single' ? (
          <Button
            variant="primary"
            onClick={handleSubmitSingle}
            disabled={!region || !scenario}
          >
            <i className="fas fa-file-download me-2" />
            Generate Report
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleSubmitMultiple}
            disabled={selectedRegions.length === 0 || !scenario}
          >
            <i className="fas fa-file-download me-2" />
            Generate {selectedRegions.length} Report{selectedRegions.length !== 1 ? 's' : ''}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default FEMAReportModal;
